// src/components/PoolSelector.js
import React, { useState, useEffect } from 'react';
import { Form, Dropdown, Button, Image } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import PoolInfoTag from './PoolInfoTag';

// Common mining pools data with preset values
const commonPools = [
    { name: 'AntPool', value: 'antpool', host: 'stratum.antpool.com', port: 3333, icon: 'antpool.svg' },
    { name: 'F2pool', value: 'f2pool', host: 'btc.f2pool.com', port: 3333, icon: 'f2pool.svg' },
    { name: 'Poolin', value: 'poolin', host: 'btc.ss.poolin.com', port: 443, icon: 'poolin.svg' },
    { name: 'ViaBTC', value: 'viabtc', host: 'btc.viabtc.io', port: 3333, icon: 'viabtc.svg' },
    { name: 'CloverPool (BTC.com)', value: 'btc', host: 'us.ss.cloverpool.com', port: 1800, icon: 'btc.svg' },
    { name: 'Binance', value: 'binance', host: 'bs.poolbinance.com', port: 3333, icon: 'binance.svg' },
    { name: 'EMCD', value: 'emcd', host: 'gate.emcd.io', port: 3333, icon: 'emcd.svg' },
    { name: 'Nicehash', value: 'nicehash', host: 'stratum.nicehash.com', port: 3353, icon: 'nicehash.svg' },
    { name: 'Trustpool', value: 'trustpool', host: 'btc.trustpool.cc', port: 3333, icon: 'trustpool.svg' },
    { name: 'Custom', value: 'custom', host: '', port: '', icon: '' }
];

const PoolSelector = ({ onSelectPool, currentHost, currentPort, onShowFields, showAddressTag = true }) => {
    const { t } = useTranslation();
    const [selectedPool, setSelectedPool] = useState('custom');
    const [showDropdown, setShowDropdown] = useState(false);

    // Find selected pool based on current host and port (for edit mode)
    useEffect(() => {
        if (!currentHost || !currentPort) {
            setSelectedPool('custom');
            if (onShowFields) onShowFields(true);
            return;
        }

        const matchedPool = commonPools.find(
            pool => pool.host === currentHost && pool.port.toString() === currentPort.toString()
        );

        if (matchedPool) {
            setSelectedPool(matchedPool.value);
            // Hide the fields if a preset pool is selected
            if (onShowFields) onShowFields(matchedPool.value === 'custom');
        } else {
            setSelectedPool('custom');
            if (onShowFields) onShowFields(true);
        }
    }, [currentHost, currentPort, onShowFields]);

    const handlePoolSelect = (value) => {
        setSelectedPool(value);
        setShowDropdown(false);

        const selectedPoolData = commonPools.find(pool => pool.value === value);

        if (selectedPoolData) {
            if (selectedPoolData.value !== 'custom') {
                // If a preset pool is selected, update the host and port values
                onSelectPool(selectedPoolData.host, selectedPoolData.port);
                // Hide the input fields
                if (onShowFields) onShowFields(false);
            } else {
                // If custom is selected, show the input fields
                if (onShowFields) onShowFields(true);
            }
        }
    };

    // Get current selected pool data
    const getSelectedPoolData = () => {
        return commonPools.find(pool => pool.value === selectedPool) || commonPools[commonPools.length - 1];
    };

    return (
        <Form.Group className="mb-3">
            <Form.Label>{t('flightSheet.selectPool')}</Form.Label>

            <Dropdown show={showDropdown} onToggle={(isOpen) => setShowDropdown(isOpen)} className="mb-3">
                <Dropdown.Toggle variant="outline-secondary" className="d-flex align-items-center justify-content-between w-100 text-start">
                    <div className="d-flex align-items-center">
                        {selectedPool !== 'custom' && (
                            <Image
                                src={`/images/pools/${getSelectedPoolData().icon}`}
                                width="20"
                                height="20"
                                className="me-2"
                                alt={getSelectedPoolData().name}
                            />
                        )}
                        {getSelectedPoolData().name}
                    </div>
                </Dropdown.Toggle>

                <Dropdown.Menu className="w-100">
                    {commonPools.map(pool => (
                        <Dropdown.Item
                            key={pool.value}
                            onClick={() => handlePoolSelect(pool.value)}
                            active={selectedPool === pool.value}
                            className="d-flex align-items-center"
                        >
                            {pool.value !== 'custom' ? (
                                <Image
                                    src={`/images/pools/${pool.icon}`}
                                    width="16"
                                    height="16"
                                    className="me-2"
                                    alt={pool.name}
                                />
                            ) : (
                                <div style={{ width: '16px', height: '16px' }} className="me-2"></div>
                            )}
                            {pool.name}
                        </Dropdown.Item>
                    ))}
                </Dropdown.Menu>
            </Dropdown>

            <Form.Text className="text-muted">
                {t('flightSheet.selectPoolInfo')}
            </Form.Text>

            {/* Show pool info tag if a pool is selected */}
            {selectedPool !== 'custom' && showAddressTag && (
                <PoolInfoTag poolData={getSelectedPoolData()} />
            )}
        </Form.Group>
    );
};

export default PoolSelector;